import React from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import {
  DashboardOverviewData,
  MetricPeriodType,
} from "api/Dashboard";
import { Panel, currency, changeText } from "./shared";

/**
 * The performance block: the period control and the four cards.
 *
 * THE CARD ROW IS FOUR CARDS, AND THE NAMES ARE NOT THE MOCKUP'S.
 * Spec section 8.2, closed 2026-09-09: the four measures are accepted
 * quotes, invoiced, collected, margin. The mockup reads "Sold, not yet
 * billed" and "Billed"; those labels are superseded. There is
 * deliberately no fifth card for work identified but not yet priced.
 *
 * THE FIRST CARD IS "APPROVED", NOT "APPROVED/ACTIVE" (Jack, Session
 * 73). A slashed label asks the reader to work out which half applies,
 * and the two halves meant the same thing anyway - a quote was
 * accepted. "Approved" is also what the plan financial cards already
 * call the same figure, so the dashboard and the plan header now use
 * one word for one measure. Spec 8.2's wording is superseded by this;
 * the DEFINITION behind it is unchanged.
 */

const PERIODS: Array<{ key: MetricPeriodType; label: string }> = [
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
  { key: "custom", label: "Custom range" },
];

export const PeriodControl: React.FC<{
  value: MetricPeriodType;
  onChange: (next: MetricPeriodType) => void;
  from?: string;
  to?: string;
  onRangeChange?: (from: string, to: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, from, to, onRangeChange, disabled }) => (
  <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
    <Flex sx={{ gap: "4px" }}>
      {PERIODS.map((p) => {
        const active = p.key === value;
        return (
          <button
            key={p.key}
            type="button"
            disabled={disabled}
            onClick={() => onChange(p.key)}
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              fontWeight: active ? 600 : 500,
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid " + themevals.colors.inputOutline,
              backgroundColor: active ? themevals.colors.base_800 : "#FFFFFF",
              color: active ? "#FFFFFF" : themevals.colors.dkgrey,
              cursor: disabled ? "default" : "pointer",
            }}
          >
            {p.label}
          </button>
        );
      })}
    </Flex>

    {/*
      The date inputs appear only on custom, and the request does not go
      out until both are set - the backend rejects a half-specified range
      with a 400, and firing on every keystroke would show the operator an
      error they are in the middle of fixing.
    */}
    {value === "custom" && onRangeChange && (
      <Flex sx={{ gap: "6px", alignItems: "center" }}>
        <input
          type="date"
          value={from || ""}
          max={to || undefined}
          disabled={disabled}
          onChange={(e) => onRangeChange(e.target.value, to || "")}
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            padding: "5px 8px",
            borderRadius: "6px",
            border: "1px solid " + themevals.colors.inputOutline,
            color: themevals.colors.base_800,
          }}
        />
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            color: themevals.colors.dkgrey,
          }}
        >
          to
        </Box>
        <input
          type="date"
          value={to || ""}
          min={from || undefined}
          disabled={disabled}
          onChange={(e) => onRangeChange(from || "", e.target.value)}
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            padding: "5px 8px",
            borderRadius: "6px",
            border: "1px solid " + themevals.colors.inputOutline,
            color: themevals.colors.base_800,
          }}
        />
      </Flex>
    )}
  </Flex>
);

const Card: React.FC<{
  title: string;
  value: string;
  sub: string;
  note?: string;
  tone?: "neutral" | "good" | "bad";
}> = ({ title, value, sub, note, tone = "neutral" }) => {
  const toneColor =
    tone === "good"
      ? themevals.colors.success
      : tone === "bad"
        ? themevals.colors.danger
        : themevals.colors.dkgrey;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: "1 1 220px",
        minWidth: "220px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "13px",
          fontWeight: 600,
          color: themevals.colors.dkgrey,
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "30px",
          fontWeight: 600,
          color: "base_800",
          lineHeight: 1.2,
          margin: "8px 0 6px",
        }}
      >
        {value}
      </Box>
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "12px",
          color: themevals.colors.dkgrey,
        }}
      >
        {sub}
      </Box>
      {note && (
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 500,
            color: toneColor,
            marginTop: "6px",
          }}
        >
          {note}
        </Box>
      )}
    </Box>
  );
};

const PerformanceBlock: React.FC<{
  data: DashboardOverviewData;
}> = ({ data }) => {
  const { approvedActive, invoiced, collected, margin } = data;

  // A period still in progress is compared against the SAME elapsed span
  // of the previous one, not all of it, so say which. Without the words,
  // "Down 100%" on the 2nd of the month reads as a collapse rather than
  // as a month that has barely started.
  const comparison = data.period.partial
    ? "vs same point last period"
    : "vs previous period";

  const collectedShare =
    invoiced.value > 0
      ? Math.round((collected.value / invoiced.value) * 100) + "% of what you billed"
      : "Nothing billed this period to collect against";

  // Margin is null when nothing costable was billed. That is not a
  // margin of zero, and it must not render as "0.0%".
  const marginValue = margin.value === null ? "n/a" : margin.value.toFixed(1) + "%";
  const marginSub =
    margin.value === null
      ? "No costed work in this period"
      : "Against " + currency(margin.cost) + " of provider cost";

  // Uncostable work is published beside the number rather than dropped.
  // A margin that silently omits work it could not cost is the confident
  // wrong number spec 7.3 exists to prevent.
  const marginNote =
    margin.uncostableCount > 0
      ? margin.uncostableCount +
        (margin.uncostableCount === 1 ? " record" : " records") +
        " could not be costed (" +
        currency(margin.uncostableRevenue) +
        "), excluded from this figure"
      : margin.changePoints === null
        ? undefined
        : (margin.changePoints >= 0 ? "Up " : "Down ") +
          Math.abs(margin.changePoints).toFixed(1) +
          " points " +
          comparison;

  return (
    <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
      <Card
        title="Approved"
        value={currency(approvedActive.value)}
        sub={approvedActive.count + (approvedActive.count === 1 ? " accepted quote" : " accepted quotes")}
        note={changeText(approvedActive.changePercent, comparison)}
        tone={
          approvedActive.changePercent === null
            ? "neutral"
            : approvedActive.changePercent >= 0
              ? "good"
              : "bad"
        }
      />
      <Card
        title="Invoiced"
        value={currency(invoiced.value)}
        sub={invoiced.count + (invoiced.count === 1 ? " invoice sent" : " invoices sent")}
        note={changeText(invoiced.changePercent, comparison)}
        tone={
          invoiced.changePercent === null
            ? "neutral"
            : invoiced.changePercent >= 0
              ? "good"
              : "bad"
        }
      />
      <Card
        title="Collected"
        value={currency(collected.value)}
        sub={collectedShare}
        note={changeText(collected.changePercent, comparison)}
        tone={
          collected.changePercent === null
            ? "neutral"
            : collected.changePercent >= 0
              ? "good"
              : "bad"
        }
      />
      <Card
        title="Margin"
        value={marginValue}
        sub={marginSub}
        note={marginNote}
        tone={margin.uncostableCount > 0 ? "bad" : "neutral"}
      />
    </Flex>
  );
};

export { Panel };
export default PerformanceBlock;
